import classNames from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// types
import { CURRENCY, ECONTRACT_STATUS, USER_ROLE } from "../../enums";
import {
  EContractType,
  ESenderRole,
  ETrasactionsType,
  IContractDetailsResponseData,
} from "../../setup/api/services/contract/models";
import { IUserInfoResponseData } from "../../setup/api/services/user/models";
import { LOCALIZE_DATE, ROUTES } from "../../setup/constants";
// providers and context
import BuyOperation from "./buy";
// constants and translations
import { REQUIRED_AMOUNT } from "./constants";
// styles
import styles from "./item.module.scss";

// components
import SellOperation from "./sell";
import { ActiveStatus } from "./status/active";
import { ArbitrationStatus } from "./status/arbitration";
import { ClosedStatus } from "./status/closed";
import { CompletedStatus } from "./status/completed";
import { WaitingInspectionStatus } from "./status/inspection";
import { ItemAcceptedStatus } from "./status/item_accepted";
import Pending from "./status/pending/pending";
import { Rejected } from "./status/rejected";
import { ResolvedStatus } from "./status/resolve";
import StockOrdered from "./status/stockOrdered/stockOrdered";
import { WaitingDeliveryStatus } from "./status/waiting_delivery";
import WaitingApproval from "./status/waitingApproval";
import { User } from "../common/user";
import Broker from "./broker";
import WaitingBuyerJoin from "./status/waiting_buyer_join/WaitingBuyerJoin";
import DepositAmount from "./status/depositAmount";
import WaitingDeliveryBroker from "./status/waiting_delivery_broker";
import BrokerRefundCustomer from "./status/broker_refund_customer";
import BrokerReleaseSeller from "./status/broker_release_seller";
import { CompletedStatusComplex } from "./status/completedComplex";
import { ExecuteInstallments } from "./status/executeInstallments";
import WaitingSellerJoin from "./status/waiting_seller_join";
import CashoutApproved from "./status/cashoutApproved";
import ReadyForCashout from "./status/readyForCashout";

export const options: Record<string, string> = {
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
};

interface Props {
  smallerSection?: boolean;
  contract: IContractDetailsResponseData;
  userId: string;
  isStockContract?: boolean;
  isCashoutRoute?: boolean;
  isTabContractRoute?: boolean;
}

const ContractsListItem = ({
  contract,
  userId,
  isStockContract = false,
  isCashoutRoute = false,
  isTabContractRoute = false,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [beneficiary, setBeneficiary] = useState<
    IUserInfoResponseData | undefined
  >(undefined);
  const isComplexContract = contract?.type === EContractType.COMPLEX_CONTRACT;

  const contractTypeStatus = useCallback(() => {
    const brokers = contract?.partiesProfiles?.BROKER;
    const providers = contract?.partiesProfiles?.PROVIDER;
    const customers = contract?.partiesProfiles?.CUSTOMER;

    if (
      brokers?.length &&
      brokers?.some((broker) => String(broker?.id) == userId)
    )
      return { userRole: USER_ROLE.BROKER };

    if (
      providers?.length &&
      providers?.some((provider) => String(provider?.id) == userId)
    )
      return {
        userRole: USER_ROLE.SELLER,
        text: `${t("lbl_broker_seller_status")}`,
      };

    if (
      customers?.length &&
      customers?.some((customer) => String(customer?.id) == userId)
    )
      return {
        userRole: USER_ROLE.BUYER,
        text: `${t("lbl_broker_provider_status")}`,
      };

    if (contract.senderRole === ESenderRole.CUSTOMER) {
      return { userRole: USER_ROLE.BUYER };
    }

    if (
      (+contract.senderId == +userId &&
        contract.senderRole === ESenderRole.PROVIDER) ||
      ((!contract?.receiverId || +contract?.receiverId == +userId) &&
        +contract?.senderId != +userId)
    ) {
      return { userRole: USER_ROLE.SELLER };
    } else {
      return { userRole: USER_ROLE.BUYER };
    }
  }, [
    contract?.partiesProfiles?.BROKER,
    contract?.partiesProfiles?.CUSTOMER,
    contract?.partiesProfiles?.PROVIDER,
    contract?.receiverId,
    contract.senderId,
    contract.senderRole,
    t,
    userId,
  ]);

  const isTabbyPayment = useMemo(() => {
    return contract.transactions.some(
      (transaction) =>
        transaction.type === ETrasactionsType.CUSTOMER_PAY &&
        transaction.paymentMethod === "TABBY"
    );
  }, [contract]);

  const getContractType = useCallback(() => {
    if (contract.senderRole === ESenderRole.BROKER) {
      const brokers = contract?.partiesProfiles?.BROKER;
      if (
        brokers?.length &&
        brokers?.some((broker) => broker?.id === Number(userId))
      ) {
        return USER_ROLE.BROKER;
      }
    }

    if (
      (contract.senderId == userId &&
        contract.senderRole === ESenderRole.PROVIDER) ||
      (contract.senderId != userId &&
        contract.senderRole === ESenderRole.CUSTOMER)
    ) {
      return USER_ROLE.SELLER;
    } else {
      return USER_ROLE.BUYER;
    }
  }, [
    contract?.partiesProfiles?.BROKER,
    contract.senderId,
    contract.senderRole,
    userId,
  ]);

  const onContractClick = useCallback(
    (contractId: string): void => {
      if (isCashoutRoute) {
        window.open(
          `${ROUTES.CASHOUTS}/${contractId}?isOperation=true&back=settlements`,
          "_blank"
        );
      } else if (isTabContractRoute) {
        window.open(
          `${ROUTES.CONTRACT}${contractId}?back=settlements`,
          "_blank"
        );
      } else {
        window.open(`${ROUTES.CONTRACT}${contractId}?back=contract`, "_blank");
      }
    },
    [isCashoutRoute, isTabContractRoute]
  );

  const renderCalculatedPrice = useCallback((): string => {
    const price =
      Number(contract?.itemPrice) + Number(contract?.shipment?.fee || 0);
    return price.toFixed(2);
  }, [contract?.itemPrice, contract?.shipment?.fee]);

  useEffect(() => {
    const customer =
      contract?.customer || contract?.partiesProfiles?.CUSTOMER?.[0];
    const provider =
      contract?.provider || contract?.partiesProfiles?.PROVIDER?.[0];
    if (contract?.senderId && customer) {
      setBeneficiary(customer as IUserInfoResponseData);
    } else {
      setBeneficiary(provider);
    }
  }, [contract, userId]);

  const imageSection = useCallback(() => {
    const imageFiles = contract?.itemDetail?.images?.filter(
      (file) => !file?.endsWith("pdf")
    );
    const imageExists = imageFiles?.length && imageFiles[0] !== "";
    return (
      <div className={styles.imgContainer}>
        <img
          className={classNames(
            !imageExists ? styles.placeholder : styles.item_image
          )}
          src={
            imageExists
              ? imageFiles[0]
              : "/assets/images/svg/contract_placeholder.svg"
          }
          alt=""
        />
      </div>
    );
  }, [contract?.itemDetail?.images]);

  const isEscalationStatus = useCallback(() => {
    return [
      ECONTRACT_STATUS.INSPECTION_ESCALATION,
      ECONTRACT_STATUS.DELIVERY_ESCALATION,
    ].includes(contract.status);
  }, [contract.status]);
  const isResolvedStatus = useCallback(() => {
    return [
      ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER,
      ECONTRACT_STATUS.RESOLVED_RELEASE_PROVIDER,
    ].includes(contract.status);
  }, [contract.status]);

  const isClosedStatus = useCallback(() => {
    return [
      ECONTRACT_STATUS.CLOSED_REFUND_CUSTOMER,
      ECONTRACT_STATUS.CLOSED_RELEASE_PROVIDER,
    ].includes(contract.status);
  }, [contract.status]);

  const isHintRequired = useCallback(() => {
    return (
      contract.status !== ECONTRACT_STATUS.COMPLETED &&
      contract.status !== ECONTRACT_STATUS.CONTRACT_REJECTED &&
      contract.status !== ECONTRACT_STATUS.PENDING_PAYMENT_VERIFICATION &&
      contract.status !== ECONTRACT_STATUS.PENDING_APPROVAL &&
      !isClosedStatus()
    );
  }, [contract.status, isClosedStatus]);

  const renderContractType = useCallback(() => {
    if (contract?.type === EContractType.COMPLEX_CONTRACT) return <></>;
    else if (contract?.type === EContractType.BROKERAGE_CONTRACT) {
      return <Broker />;
    } else if (
      contract.type === EContractType.QUICK_TRANSACTION &&
      contract.senderRole === ESenderRole.CUSTOMER
    ) {
      return <BuyOperation />;
    }
    return <SellOperation />;
  }, [contract]);

  const detailsSection = useCallback(() => {
    return (
      <div className={styles.contract_info}>
        <div className={styles.status_wrapper}>
          <>{renderContractType()}</>

          {/* contract status */}
          {contract?.status === ECONTRACT_STATUS.SUBMITTED && (
            <>
              {contract?.type === EContractType.BROKERAGE_CONTRACT ? (
                <WaitingBuyerJoin />
              ) : contract?.senderRole === ESenderRole.CUSTOMER &&
                !contract?.receiverId ? (
                <WaitingSellerJoin />
              ) : (
                <ActiveStatus />
              )}
            </>
          )}
          {contract?.status === ECONTRACT_STATUS.CONTRACT_ACCEPTED && (
            <DepositAmount />
          )}
          {contract?.status === ECONTRACT_STATUS.CASH_OUT_APPROVED && (
            <CashoutApproved />
          )}
          {contract?.status === ECONTRACT_STATUS.PARTIAL_CASHOUT && (
            <CashoutApproved />
          )}
          {contract?.status === ECONTRACT_STATUS.READY_FOR_CASH_OUT && (
            <ReadyForCashout />
          )}
          {contract.status === ECONTRACT_STATUS.CREATED && <ActiveStatus />}
          {(contract.status === ECONTRACT_STATUS.COMPLETED ||
            contract.status === ECONTRACT_STATUS.EXCEPTIONAL_COMPLETED) && (
            <>
              {contract?.type === EContractType.COMPLEX_CONTRACT ? (
                <CompletedStatusComplex />
              ) : (
                <CompletedStatus />
              )}
            </>
          )}
          {contract.status === ECONTRACT_STATUS.WAITING_FOR_DELIVERY && (
            <>
              {contract?.type === EContractType.BROKERAGE_CONTRACT ? (
                <WaitingDeliveryBroker />
              ) : (
                <WaitingDeliveryStatus />
              )}
            </>
          )}
          {contract?.type === EContractType.COMPLEX_CONTRACT &&
            contract.status === ECONTRACT_STATUS.IN_PROGRESS && (
              <ExecuteInstallments />
            )}

          {contract.status === ECONTRACT_STATUS.ITEM_INSPECTION && (
            <WaitingInspectionStatus />
          )}
          {contract.status === ECONTRACT_STATUS.ITEM_ACCEPTED && (
            <>
              {contract?.type === EContractType.QUICK_TRANSACTION ? (
                <ReadyForCashout />
              ) : (
                <ItemAcceptedStatus />
              )}
            </>
          )}
          {contract.status === ECONTRACT_STATUS.CONTRACT_REJECTED && (
            <Rejected />
          )}
          {contract.status === ECONTRACT_STATUS.OUT_OF_STOCK && (
            <StockOrdered />
          )}
          {contract.status === ECONTRACT_STATUS.PENDING_APPROVAL && <Pending />}
          {contract.status ===
            ECONTRACT_STATUS.PENDING_PAYMENT_VERIFICATION && (
            <WaitingApproval />
          )}
          {contract?.type === EContractType.BROKERAGE_CONTRACT ? (
            <>
              {contract.status ===
                ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER && (
                <BrokerRefundCustomer />
              )}
              {contract.status ===
                ECONTRACT_STATUS.RESOLVED_RELEASE_PROVIDER && (
                <BrokerReleaseSeller />
              )}
            </>
          ) : (
            <>{isResolvedStatus() && <ResolvedStatus />}</>
          )}

          {isClosedStatus() && <ClosedStatus />}
          {isEscalationStatus() && <ArbitrationStatus />}
        </div>
        <div className={styles.header_wrapper}>
          <h4 className={styles.header_title}>{contract.itemDetail?.title}</h4>
          <p
            className={classNames(
              styles["hint2"],
              styles["mr1"],
              styles["ml1"],
              styles["mv0"]
            )}>
            .
          </p>
          <p className={classNames(styles["hint2"], styles["mv0"])}>
            {new Date(contract.createdAt).toLocaleDateString(
              LOCALIZE_DATE,
              options
            )}
          </p>
        </div>
        <p className={classNames("p2", styles.contractDescription)}>
          {contract.itemDetail?.description}
        </p>
      </div>
    );
  }, [
    contract.createdAt,
    contract.itemDetail?.description,
    contract.itemDetail?.title,
    contract?.receiverId,
    contract?.senderRole,
    contract.status,
    contract?.type,
    contractTypeStatus,
    isClosedStatus,
    isEscalationStatus,
    isResolvedStatus,
    renderContractType,
  ]);

  const hintSection = useCallback(() => {
    return (
      <div>
        {contract.status === ECONTRACT_STATUS.CREATED &&
          getContractType() === USER_ROLE.SELLER &&
          !isStockContract && (
            <div className={styles.hint}>
              <img alt="copy" src="/assets/images/svg/copyIcon.svg" />
              <p className="p3">{t("lbl_listing_active_disclaimer")}</p>
            </div>
          )}
        {contract.status === ECONTRACT_STATUS.WAITING_FOR_DELIVERY &&
          getContractType() === USER_ROLE.SELLER && (
            <div className={styles.hint}>
              <img alt="delivery" src="/assets/images/svg/deliveryIcon.svg" />
              <p className="p3">
                {t("lbl_listing_waiting_for_delivery_disclaimer_seller")}
              </p>
            </div>
          )}
        {contract.status === ECONTRACT_STATUS.WAITING_FOR_DELIVERY &&
          getContractType() === USER_ROLE.BUYER && (
            <div className={styles.hint}>
              <img alt="delivery" src="/assets/images/svg/deliveryIcon.svg" />
              <p className="p3">
                {t("lbl_listing_waiting_for_delivery_disclaimer_buyer")}
              </p>
            </div>
          )}
        {contract.status === ECONTRACT_STATUS.SUBMITTED &&
          getContractType() === USER_ROLE.BUYER && (
            <div className={styles.hint}>
              <img alt="copy" src="/assets/images/svg/copyIcon.svg" />
              <p className="p3">{t("lbl_listing_active_disclaimer_buyer")}</p>
            </div>
          )}
        {contract.status === ECONTRACT_STATUS.ITEM_INSPECTION &&
          getContractType() === USER_ROLE.SELLER && (
            <div className={styles.hint}>
              <img alt="loading" src="/assets/images/svg/loadingIcon.svg" />
              <p className="p3">
                {t("lbl_listing_waiting_for_inspection_disclaimer_seller")}
              </p>
            </div>
          )}
        {contract.status === ECONTRACT_STATUS.ITEM_INSPECTION &&
          getContractType() === USER_ROLE.BUYER && (
            <div className={styles.hint}>
              <img alt="checking" src="/assets/images/svg/checkingIcon.svg" />
              <p className="p3">
                {t("lbl_listing_waiting_for_inspection_disclaimer_buyer")}
              </p>
            </div>
          )}
        {contract.status === ECONTRACT_STATUS.ITEM_ACCEPTED && (
          <>
            {contract?.type === EContractType.QUICK_TRANSACTION ? (
              <div className={styles.hint}>
                <img
                  alt="loading"
                  src="/assets/images/svg/yellow_loading.svg"
                />
                <p className="p3">{t("status_settlement_subtitle")}</p>
              </div>
            ) : getContractType() === USER_ROLE.SELLER ? (
              <div className={styles.hint}>
                <img alt="loading" src="/assets/images/svg/loadingIcon.svg" />
                <p className="p3">
                  {t("lbl_listing_item_accepted_disclaimer_seller")}
                </p>
              </div>
            ) : (
              <div className={styles.hint}>
                <img alt="loading" src="/assets/images/svg/loadingIcon.svg" />
                <p className="p3">
                  {t("lbl_listing_item_accepted_disclaimer_buyer")}
                </p>
              </div>
            )}
          </>
        )}
        {isEscalationStatus() && (
          <div className={styles.hint}>
            <img alt="loading" src="/assets/images/svg/loadingIcon.svg" />
            <p className="p3">{t("lbl_listing_escalated_disclaimer")}</p>
          </div>
        )}

        {contract.status === ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER &&
          getContractType() === USER_ROLE.SELLER && (
            <div className={styles.hint}>
              <img alt="loading" src="/assets/images/svg/loadingIcon.svg" />
              <p className="p3">
                {t("lbl_listing_resolved_buyer_refund_disclaimer_seller")}
              </p>
            </div>
          )}
        {contract.status === ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER &&
          getContractType() === USER_ROLE.BUYER && (
            <div className={styles.hint}>
              <img alt="loading" src="/assets/images/svg/loadingIcon.svg" />
              <p className="p3">
                {t("lbl_listing_resolved_buyer_refund_disclaimer_buyer")}
              </p>
            </div>
          )}
        {contract.status === ECONTRACT_STATUS.RESOLVED_RELEASE_PROVIDER &&
          getContractType() === USER_ROLE.SELLER && (
            <div className={styles.hint}>
              <img alt="loading" src="/assets/images/svg/loadingIcon.svg" />
              <p className="p3">
                {t("lbl_listing_resolved_seller_release_disclaimer_seller")}
              </p>
            </div>
          )}
        {contract.status === ECONTRACT_STATUS.RESOLVED_RELEASE_PROVIDER &&
          getContractType() === USER_ROLE.BUYER && (
            <div className={styles.hint}>
              <img alt="loading" src="/assets/images/svg/loadingIcon.svg" />
              <p className="p3">
                {t("lbl_listing_resolved_seller_release_disclaimer_buyer")}
              </p>
            </div>
          )}
        {contract.status === ECONTRACT_STATUS.CREATED &&
          getContractType() === USER_ROLE.SELLER &&
          isStockContract && (
            <div className={styles.hint}>
              {contract.isParent && contract?.activeChildrenCount ? (
                <img alt="active" src="/assets/images/svg/activeIcon.svg" />
              ) : (
                <img alt="copy" src="/assets/images/svg/copyIcon.svg" />
              )}
              <p className="p3">
                {contract.isParent && contract?.activeChildrenCount
                  ? t("lbl_listing_active_stock_contract_disclaimer", {
                      val: contract?.activeChildrenCount,
                    })
                  : t("lbl_listing_active_disclaimer")}
              </p>
            </div>
          )}
        {contract.status === ECONTRACT_STATUS.OUT_OF_STOCK &&
          getContractType() === USER_ROLE.SELLER &&
          isStockContract && (
            <div className={styles.hint}>
              <img alt="warning" src="/assets/images/svg/warningIcon.svg" />
              <p className="p3">
                {t("lbl_listing_out_of_stock_contract_disclaimer")}
              </p>
            </div>
          )}
        {contract.status === ECONTRACT_STATUS.READY_FOR_CASH_OUT && (
          <div className={styles.hint}>
            <img alt="loading" src="/assets/images/svg/yellow_loading.svg" />
            <p className="p3">{t("status_settlement_subtitle")}</p>
          </div>
        )}
        {(contract.status === ECONTRACT_STATUS.CASH_OUT_APPROVED ||
          contract.status === ECONTRACT_STATUS.PARTIAL_CASHOUT) && (
          <div className={styles.hint}>
            <img alt="loading" src="/assets/images/svg/yellow_loading.svg" />
            <p className="p3">{t("status_transfer_subtitle")}</p>
          </div>
        )}
      </div>
    );
  }, [
    contract?.activeChildrenCount,
    contract?.isParent,
    contract.status,
    contract.type,
    getContractType,
    isEscalationStatus,
    isStockContract,
    t,
  ]);

  const profileSection = useCallback(() => {
    return (
      <div className={styles.profile_section}>
        <div className={styles.priceContainer}>
          {!isComplexContract && (
            <>
              <div>
                {beneficiary && (
                  <User
                    name={beneficiary?.profile?.firstName}
                    phoneNumber={beneficiary?.phoneNumber}
                    imageUrl={beneficiary?.profile?.photoUrl}
                  />
                )}
              </div>
              {beneficiary && <div className={styles.divider} />}
            </>
          )}

          <div className={styles.priceSection}>
            {isTabbyPayment && (
              <img
                src="/assets/images/svg/tabby.svg"
                alt="tabby"
                width="66px"
                height="24px"
                style={{ marginBottom: "45px" }}
              />
            )}
            <div className={isComplexContract ? "subtitle2 g500" : "hint2"}>
              {isComplexContract ? t("PRICE_INSTALLMENTS") : REQUIRED_AMOUNT}
            </div>
            <span className={"subtitle1"}>
              {isComplexContract
                ? contract?.activeChildrenCount
                : renderCalculatedPrice()}
            </span>
            <span> </span>
            <span className={isComplexContract ? "subtitle2 g500" : "hint2"}>
              {isComplexContract ? t("INSTALLMENTS") : CURRENCY.SAR}
            </span>
          </div>
        </div>
      </div>
    );
  }, [
    beneficiary,
    contract?.activeChildrenCount,
    isComplexContract,
    isTabbyPayment,
    renderCalculatedPrice,
    t,
  ]);

  const navigationSection = useCallback(() => {
    return (
      <div className={styles.contract_navigation}>
        <img src="/assets/images/svg/contractNavigation.svg" alt="navigation" />
      </div>
    );
  }, []);

  return (
    <div
      key={contract.id}
      onClick={() => {
        onContractClick(contract.id);
      }}>
      <div
        className={classNames(styles["card"], {
          [styles.card]: isHintRequired(),
        })}>
        <div className={styles.contract_link}>
          <div className={styles.left_section}>
            {imageSection()}
            {detailsSection()}
          </div>
          <div className={styles.right_section}>
            {profileSection()}
            {navigationSection()}
          </div>
        </div>
      </div>
      {isHintRequired() && (
        <div className={styles.hint_section}>
          {hintSection()}
          {navigationSection()}
        </div>
      )}
    </div>
  );
};

export default ContractsListItem;
